import { useQuery } from "@tanstack/react-query";
import { getCloudCostProjection, getCloudCostTrends } from "@/helpers/apis";
import { CLOUD_TOTAL_KEY } from "./cloudCostTaxonomy";
import type { CloudCostProjectionParams, CloudCostTrendsParams } from "./cloudCostTaxonomy";

// Cloud infrastructure cost (full cloud TCO) by category over the selected
// range — backs the Summary "Cloud Costs" past lens and the Jobs "Cloud Costs"
// group-by chart. Hits `GET /databricks/cost/cloud/trends`.
export const useCloudCostTrends = (params: CloudCostTrendsParams, enabled = true) =>
  useQuery({
    queryKey: [
      "databricks-cloud-cost-trends",
      params.start_date,
      params.end_date,
      params.granularity,
      params.workspace_id,
    ],
    queryFn: () => getCloudCostTrends(params),
    enabled: enabled && Boolean(params.start_date && params.end_date),
  });

// Actual + projected cloud cost across the calendar year — backs the Summary
// "Cloud Costs" future lens. Hits `GET /databricks/cost/cloud/projection`.
export const useCloudCostProjection = (params: CloudCostProjectionParams, enabled = true) =>
  useQuery({
    queryKey: ["databricks-cloud-cost-projection", params.workspace_id],
    queryFn: () => getCloudCostProjection(params),
    enabled,
  });

interface CloudCostTotal {
  total: number;
  isLoading: boolean;
}

// Summed cloud infra cost over the window — backs the Summary "Cloud Costs"
// totals tile, which renders only when the window actually has cost. Shares
// the trends query above (same key) so it adds no extra request. Returns the
// loading state too so the tile can wait for it and avoid a pop-in.
export const useCloudCostTrendsTotal = (
  params: CloudCostTrendsParams,
  enabled = true
): CloudCostTotal => {
  const { data, isLoading } = useCloudCostTrends(params, enabled);
  const total = (data ?? []).reduce((sum, row) => sum + (Number(row[CLOUD_TOTAL_KEY]) || 0), 0);
  return { total, isLoading: enabled && isLoading };
};

// Full-year projected cloud cost (actual + estimate) for the Future State
// totals tile. Shares the projection query above.
export const useCloudCostProjectionTotal = (
  params: CloudCostProjectionParams,
  enabled = true
): CloudCostTotal => {
  const { data, isLoading } = useCloudCostProjection(params, enabled);
  const total = (data ?? []).reduce(
    (sum, row) => sum + (row.total ?? 0) + (row.total_estimate ?? 0),
    0
  );
  return { total, isLoading: enabled && isLoading };
};
